import { useEffect, useRef, useState } from 'react';
import { Check, MessageSquare, RotateCw, Download, Film, Clock, Monitor, FileVideo, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTH_TOKEN_KEY = 'alivan-auth-token';
const authHeaders = () => {
  try {
    const t = localStorage.getItem(AUTH_TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch (_) { return {}; }
};

const humanSize = (b) => {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const humanDuration = (s) => {
  if (s == null) return '—';
  const secs = Math.round(s);
  const m = Math.floor(secs / 60);
  const r = secs % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};

/**
 * Fetch an artifact authenticated and turn it into a same-origin Blob URL.
 * NEVER pass the auth token in the URL — it would leak into browser history,
 * server access logs and Referer headers.
 */
const fetchAsObjectUrl = async (jobId, kind, version) => {
  const url = `${API}/production/projects/${jobId}/artifact?kind=${kind}&v=${version}`;
  const r = await fetch(url, {
    credentials: 'include',
    headers: { ...authHeaders() },
  });
  if (!r.ok) throw new Error(`artifact_fetch_failed:${r.status}`);
  const blob = await r.blob();
  return URL.createObjectURL(blob);
};

const DraftPlayer = ({ jobId, draft, aiNotes, onApprove, onRequestChanges, onReRender }) => {
  const [changeText, setChangeText] = useState('');
  const [showChanges, setShowChanges] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const mounted = useRef(true);
  const currentUrls = useRef({ video: null, thumb: null });

  const revokeAll = () => {
    for (const key of ['video', 'thumb']) {
      const u = currentUrls.current[key];
      if (u) {
        try { URL.revokeObjectURL(u); } catch (_) { /* noop */ }
      }
      currentUrls.current[key] = null;
    }
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      revokeAll();
    };
  }, []);

  // Load / reload blobs whenever the draft version changes.
  useEffect(() => {
    if (!jobId || !draft?.version) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError('');
      // Revoke previous blob URLs from an earlier version.
      revokeAll();
      setVideoUrl(null);
      setThumbUrl(null);
      try {
        // Fire thumbnail first (small) and video in parallel.
        const [thumbP, videoP] = [
          draft.thumbnail_path
            ? fetchAsObjectUrl(jobId, 'thumbnail', draft.version).catch(() => null)
            : Promise.resolve(null),
          fetchAsObjectUrl(jobId, 'video', draft.version),
        ];
        const t = await thumbP;
        if (!cancelled && mounted.current && t) {
          currentUrls.current.thumb = t;
          setThumbUrl(t);
        } else if (t) {
          // Component unmounted or draft changed → revoke.
          URL.revokeObjectURL(t);
        }
        const v = await videoP;
        if (!cancelled && mounted.current) {
          currentUrls.current.video = v;
          setVideoUrl(v);
        } else {
          URL.revokeObjectURL(v);
        }
      } catch (e) {
        if (!cancelled && mounted.current) {
          setLoadError('No pudimos cargar el video del borrador.');
        }
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, draft?.version, draft?.thumbnail_path]);

  const handleDownload = async () => {
    if (!jobId || !draft?.version || downloading) return;
    setDownloading(true);
    try {
      // Fetch again (or reuse videoUrl blob) and trigger a same-origin
      // download without ever putting the token in the URL.
      let url = videoUrl;
      let createdHere = false;
      if (!url) {
        url = await fetchAsObjectUrl(jobId, 'video', draft.version);
        createdHere = true;
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = `alivan_draft_v${draft.version}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (createdHere) {
        // Give the browser a tick to start the download before revoking.
        setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 4000);
      }
    } catch (e) {
      setLoadError('La descarga falló.');
    } finally {
      setDownloading(false);
    }
  };

  if (!draft) return null;
  const notes = draft.ai_notes || aiNotes || '';

  return (
    <div className="border border-neon-red/30 bg-black/40 rounded p-3 mt-3" data-testid="prod-draft-player">
      <div className="flex items-center gap-2 mb-2">
        <Film className="w-4 h-4 text-neon-red" />
        <div className="text-[10px] font-jetbrains uppercase tracking-widest text-neon-red">
          Borrador v{draft.version} · listo para revisar
        </div>
        {loading && (
          <span className="ml-2 text-[10px] text-white/50 flex items-center gap-1" data-testid="prod-draft-loading">
            <Loader2 className="w-3 h-3 animate-spin" />cargando…
          </span>
        )}
      </div>

      {videoUrl ? (
        <video
          data-testid="prod-draft-video"
          src={videoUrl}
          poster={thumbUrl || undefined}
          controls
          preload="metadata"
          className="w-full max-h-[420px] bg-black rounded border border-white/10"
        />
      ) : loadError ? (
        <div className="text-[11px] text-red-300 border border-red-500/40 bg-red-500/10 rounded p-2" data-testid="prod-draft-load-error">
          {loadError}
        </div>
      ) : (
        <div className="text-[11px] text-white/50 italic">Preparando reproductor…</div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[11px]">
        <div className="border border-white/10 rounded p-2">
          <div className="text-[9px] uppercase text-white/40 tracking-widest font-jetbrains flex items-center gap-1">
            <Clock className="w-3 h-3" />Duración
          </div>
          <div className="text-white/90 mt-0.5" data-testid="prod-draft-duration">{humanDuration(draft.duration_s)}</div>
        </div>
        <div className="border border-white/10 rounded p-2">
          <div className="text-[9px] uppercase text-white/40 tracking-widest font-jetbrains flex items-center gap-1">
            <Monitor className="w-3 h-3" />Resolución
          </div>
          <div className="text-white/90 mt-0.5" data-testid="prod-draft-resolution">{draft.resolution || '—'}</div>
        </div>
        <div className="border border-white/10 rounded p-2">
          <div className="text-[9px] uppercase text-white/40 tracking-widest font-jetbrains flex items-center gap-1">
            <FileVideo className="w-3 h-3" />Tamaño
          </div>
          <div className="text-white/90 mt-0.5" data-testid="prod-draft-size">{humanSize(draft.size_bytes)}</div>
        </div>
        <div className="border border-white/10 rounded p-2">
          <div className="text-[9px] uppercase text-white/40 tracking-widest font-jetbrains">FPS · Codec</div>
          <div className="text-white/90 mt-0.5">{draft.fps || '—'} · {draft.codec || '—'}</div>
        </div>
      </div>

      {notes && (
        <div className="mt-3 border border-neon-red/20 bg-black/30 rounded p-2" data-testid="prod-draft-notes">
          <div className="text-[9px] uppercase font-jetbrains text-neon-red/80 tracking-widest mb-1">
            Notas de AliVan
          </div>
          <div className="text-[11px] text-white/80 whitespace-pre-wrap">{notes}</div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          data-testid="prod-draft-approve"
          onClick={onApprove}
          className="text-[11px] px-3 py-1.5 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20 rounded flex items-center gap-1"
        >
          <Check className="w-3 h-3" />Aprobar borrador
        </button>
        <button
          data-testid="prod-draft-request-changes"
          onClick={() => setShowChanges(s => !s)}
          className="text-[11px] px-3 py-1.5 border border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/20 rounded flex items-center gap-1"
        >
          <MessageSquare className="w-3 h-3" />Solicitar cambios
        </button>
        <button
          data-testid="prod-draft-re-render"
          onClick={onReRender}
          className="text-[11px] px-3 py-1.5 border border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20 rounded flex items-center gap-1"
        >
          <RotateCw className="w-3 h-3" />Re-renderizar
        </button>
        <button
          data-testid="prod-draft-download"
          onClick={handleDownload}
          disabled={downloading}
          className="text-[11px] px-3 py-1.5 border border-white/20 text-white/70 hover:bg-white/10 rounded flex items-center gap-1 disabled:opacity-50"
        >
          {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
          Descargar
        </button>
      </div>

      {showChanges && (
        <div className="mt-3" data-testid="prod-draft-changes-panel">
          <label className="text-[10px] uppercase font-jetbrains text-neon-red/80 tracking-widest">
            ¿Qué debería cambiar?
          </label>
          <textarea
            data-testid="prod-draft-changes-input"
            value={changeText}
            onChange={(e) => setChangeText(e.target.value)}
            rows={3}
            placeholder="Ej: los cortes al minuto 0:12 son bruscos, alarga la escena final…"
            className="w-full mt-1 bg-black/60 border border-white/20 rounded p-2 text-xs text-white"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              onClick={() => { setShowChanges(false); setChangeText(''); }}
              className="text-[11px] px-3 py-1 border border-white/20 text-white/60 rounded"
              data-testid="prod-draft-changes-cancel"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                const txt = changeText.trim();
                if (!txt) return;
                onRequestChanges?.(txt);
                setChangeText('');
                setShowChanges(false);
              }}
              disabled={!changeText.trim()}
              className="text-[11px] px-3 py-1 border border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/20 rounded disabled:opacity-40"
              data-testid="prod-draft-changes-submit"
            >
              Enviar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftPlayer;
